import { friendlyMessage } from '../../../../components/ui/friendlyMessage';
import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../../components/common/NoData';

import DefaultImage from '../../../../assets/images/profile/default-profile.png';
import { getCourseReviews } from '../../../../services/organization/courses/courseContent.service';
import { useTimezone } from '../../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../../utils/timezone';

function CourseRating({ contentData }) {
  const { timeZone } = useTimezone();
  const formatRatingDate = (value) => formatUtcInTimeZone(value, timeZone, 'MMM DD, YYYY');
  const [ratings, setRatings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const videoId = contentData?.id;
  const courseId = contentData?.course_id;


  useEffect(() => {
    let isMounted = true;

    const fetchReviews = async () => {
      try {
        if (!videoId || !courseId) {
          setError('Missing required video or course ID');
          setIsLoading(false);
          return;
        }


        const response = await getCourseReviews(courseId, videoId);

        if (!isMounted) return;

        if (response?.success) {
          setRatings(response.data || []);
          setError(null);
        } else {
          console.error('API returned unsuccessful response:', response);
          setError('Failed to fetch reviews');
          setRatings([]);
        }
      } catch (error) {
        console.error('Error in fetchReviews:', error);
        if (isMounted) {
          setError(error.message || 'Failed to load reviews');
          setRatings([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    fetchReviews();

    return () => {
      isMounted = false;
    };
  }, [videoId, courseId]);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        className={`star   ${index < rating ? "active" : ""}`}
      >
        <Icon icon="mdi:star" width="24" height="24" />
      </span>
    ));
  };

  if (isLoading) {
    return (
      <div className="tw-text-center tw-p-6">
        <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
          <span className="tw-sr-only">Loading reviews...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] tw-bg-[rgb(255_251_235_/_var(--tw-bg-opacity,_1))] tw-border-[rgb(254_243_199_/_var(--tw-border-opacity,_1))] [--tw-border-opacity:1] [--tw-bg-opacity:1] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-danger tw-m-4" role="alert">
        {friendlyMessage(error)}
      </div>
    );
  }

  if (!videoId || !courseId) {
    return (
      <div className="tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-border-opacity:1] tw-border-[rgb(254_243_199_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_251_235_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-warning tw-m-4" role="alert">
        Missing required video or course information
      </div>
    );
  }

  return (
    <div className="max-[767px]:tw-min-h-[auto] tw-bg-[#fff] tw-min-h-[calc(100vh_-_400px)] tw-flex tw-flex-col course-rating-container tw-p-0">
      <div className={"max-[767px]:tw-max-h-[none] max-[767px]:tw-overflow-y-visible max-[767px]:tw-pr-0 [flex:1] tw-overflow-y-auto tw-max-h-[calc(100vh_-_550px)] tw-pr-[10px] [&::-webkit-scrollbar]:tw-w-[6px] [&::-webkit-scrollbar-track]:tw-bg-[#f1f1f1] [&::-webkit-scrollbar-track]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb]:tw-bg-[#c1c1c1] [&::-webkit-scrollbar-thumb]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[#a8a8a8] ratings-list"}>
        {Array.isArray(ratings) && ratings.length > 0 ? (
          ratings.map(rating => {
            return (
              <div key={rating.review_id || `rating-${Math.random()}`} className={"tw-bg-[#fff] tw-border-[1px] tw-border-solid tw-border-[#e0e0e0] tw-rounded-[8px] tw-p-[16px] tw-mb-[16px] [transition:transform_0.2s_ease,_box-shadow_0.2s_ease] [&:hover]:[transform:translateY(-2px)] [&:hover]:tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.05)] rating-item"}>
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-[12px] rating-item-header">
                  <div className="tw-flex tw-items-center tw-gap-[12px] rating-author">
                    <img
                      src={rating.user?.profile_picture || DefaultImage}
                      alt="avatar"
                      className="tw-w-[40px] tw-h-[40px] tw-rounded-[50%] tw-object-cover tw-border-[2px] tw-border-solid tw-border-[#f0f0f0] author-avatar"
                    />
                    <span className="tw-font-semibold tw-text-[#333] tw-text-[15px] author-name">{rating.user?.name || 'Anonymous'}</span>
                  </div>
                  <span className="tw-text-[#666] tw-text-[13px] rating-date">
                    {rating.review_createdAt ? formatRatingDate(rating.review_createdAt) : 'No date'}
                  </span>
                </div>
                <div className={"tw-flex tw-gap-[5px] tw-m-[12px_0] [&_.star-btn]:tw-cursor-default rating-stars-display"}>
                  {renderStars(rating.class_rating || 0)}
                </div>
                {rating.review_text && <p className="tw-m-[12px_0_0_0] tw-text-[#444] tw-text-[14px] tw-whitespace-pre-wrap rating-text">{rating.review_text}</p>}
              </div>
            );
          })
        ) : (
          <NoData
            message="No Ratings Available"
            description="No ratings have been submitted for this content yet."
            icon="mdi:star-outline"
          />
        )}
      </div>
    </div>
  );
}

export default CourseRating;